import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  GripVertical,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Save,
  ArrowLeft,
  ExternalLink,
  Upload,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import ImageUpload from '../components/ImageUpload';

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero Section' },
  { value: 'cta_strip', label: 'CTA Strip / Stats' },
  { value: 'feature_grid', label: 'Feature Grid' },
  { value: 'text', label: 'Text Section' },
  { value: 'image_text', label: 'Image + Text' },
  { value: 'video_text', label: 'Video + Text' },
  { value: 'embed', label: 'Embed (General)' },
  { value: 'gallery', label: 'Image Gallery' },
  { value: 'sponsors', label: 'Sponsors / Partners' },
  { value: 'press', label: 'Press / Media Coverage' },
  { value: 'newsletter_flodesk', label: 'Newsletter (Flodesk)' },
];

const PAGE_TYPES = {
  regular: { label: 'Regular', color: 'bg-gray-500' },
  expo: { label: 'Expo', color: 'bg-blue-500' },
  festival: { label: 'Festival', color: 'bg-purple-500' },
};

// Sortable Section Component
function SortableSection({ section, onEdit, onDelete, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className="p-4 mb-3" data-testid={`section-${section.id}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div {...listeners} className="cursor-move">
              <GripVertical className="text-gray-400" size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900">
                  {section.title || `${section.type} Section`}
                </h3>
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                  {section.type}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Order: {section.order}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => onToggle(section.id, !section.is_enabled)}
              variant="ghost"
              size="sm"
              title={section.is_enabled ? 'Hide' : 'Show'}
            >
              {section.is_enabled ? (
                <Eye size={18} className="text-green-600" />
              ) : (
                <EyeOff size={18} className="text-gray-400" />
              )}
            </Button>
            <Button onClick={() => onEdit(section)} variant="ghost" size="sm">
              <Edit size={18} />
            </Button>
            <Button
              onClick={() => onDelete(section.id)}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Section Editor Component
function SectionEditor({ section, onSave, onCancel }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    type: section?.type || 'hero',
    title: section?.title || '',
    anchor: section?.anchor || '',
    data: section?.data || {},
  });

  const handleSave = () => {
    onSave(formData);
  };

  const updateData = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      data: { ...prev.data, [key]: value },
    }));
  };

  const renderFields = () => {
    // Common anchor field for all section types
    const anchorField = (
      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
        <label className="block text-sm font-medium text-gray-700 mb-1">Section Anchor (ID)</label>
        <Input
          value={formData.anchor || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, anchor: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))}
          placeholder="e.g., about, speakers, schedule"
          className="font-mono text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Used for menu links (e.g., #about). Letters, numbers, and hyphens only.</p>
      </div>
    );

    switch (formData.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Headline</label>
              <Input
                value={formData.data.headline || ''}
                onChange={(e) => updateData('headline', e.target.value)}
                placeholder="Page Headline"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Date</label>
              <Input
                value={formData.data.event_date || ''}
                onChange={(e) => updateData('event_date', e.target.value)}
                placeholder="February 11, 2026"
              />
              <p className="text-xs text-gray-500 mt-1">Displayed prominently below headline in accent color</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subheadline</label>
              <Textarea
                value={formData.data.subheadline || ''}
                onChange={(e) => updateData('subheadline', e.target.value)}
                placeholder="Supporting text..."
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <Textarea
                value={formData.data.description || ''}
                onChange={(e) => updateData('description', e.target.value)}
                placeholder="Longer description..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Label</label>
                <Input
                  value={formData.data.primary_cta_label || ''}
                  onChange={(e) => updateData('primary_cta_label', e.target.value)}
                  placeholder="Get Started"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Link</label>
                <Input
                  value={formData.data.primary_cta_link || ''}
                  onChange={(e) => updateData('primary_cta_link', e.target.value)}
                  placeholder="#section"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Label</label>
                <Input
                  value={formData.data.secondary_cta_label || ''}
                  onChange={(e) => updateData('secondary_cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Link</label>
                <Input
                  value={formData.data.secondary_cta_link || ''}
                  onChange={(e) => updateData('secondary_cta_link', e.target.value)}
                  placeholder="#about"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Badge Text (optional)</label>
              <Input
                value={formData.data.badge_text || ''}
                onChange={(e) => updateData('badge_text', e.target.value)}
                placeholder="1,000+ Attendees"
              />
            </div>
            <ImageUpload
              value={formData.data.background_image || ''}
              onChange={(url) => updateData('background_image', url)}
              label="Background Image (optional)"
            />
          </div>
        );

      case 'cta_strip':
        return (
          <div className="space-y-4">
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
              <Input
                value={formData.data.section_title || ''}
                onChange={(e) => updateData('section_title', e.target.value)}
                placeholder="Stay in the Loop"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
              <Textarea
                value={formData.data.section_description || ''}
                onChange={(e) => updateData('section_description', e.target.value)}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Stats (for Stats Section)</label>
              {(formData.data.stats || []).map((stat, index) => (
                <div key={index} className="grid grid-cols-3 gap-2 mb-2">
                  <Input
                    placeholder="Number"
                    value={stat.number}
                    onChange={(e) => {
                      const stats = [...(formData.data.stats || [])];
                      stats[index] = { ...stats[index], number: e.target.value };
                      updateData('stats', stats);
                    }}
                  />
                  <Input
                    placeholder="Label"
                    value={stat.label}
                    onChange={(e) => {
                      const stats = [...(formData.data.stats || [])];
                      stats[index] = { ...stats[index], label: e.target.value };
                      updateData('stats', stats);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const stats = (formData.data.stats || []).filter((_, i) => i !== index);
                      updateData('stats', stats);
                    }}
                    className="text-red-600"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
              <Button
                onClick={() => {
                  const stats = [...(formData.data.stats || []), { number: '', label: '' }];
                  updateData('stats', stats);
                }}
                variant="outline"
                size="sm"
                className="w-full mt-2"
              >
                <Plus size={16} className="mr-2" />
                Add Stat
              </Button>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'feature_grid':
        return (
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="speakers">Speakers</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
              <TabsTrigger value="tickets">Tickets</TabsTrigger>
              <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
              <TabsTrigger value="faqs">FAQs</TabsTrigger>
              <TabsTrigger value="steps">Steps</TabsTrigger>
            </TabsList>
            
            {/* General Tab */}
            <TabsContent value="general" className="space-y-4">
              {anchorField}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <Input
                  value={formData.data.section_title || ''}
                  onChange={(e) => updateData('section_title', e.target.value)}
                  placeholder="Our Features"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
                <Textarea
                  value={formData.data.section_description || ''}
                  onChange={(e) => updateData('section_description', e.target.value)}
                  rows={3}
                  placeholder="Brief description shown centered above content"
                />
                <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                  <Input
                    value={formData.data.cta_label || ''}
                    onChange={(e) => updateData('cta_label', e.target.value)}
                    placeholder="Learn More"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                  <Input
                    value={formData.data.cta_link || ''}
                    onChange={(e) => updateData('cta_link', e.target.value)}
                    placeholder="#section or https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
                </div>
              </div>
            </TabsContent>
            
            {/* Features Tab */}
            <TabsContent value="features" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add feature cards with icons</p>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.features || []).map((feature, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Feature {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const features = (formData.data.features || []).filter((_, i) => i !== index);
                            updateData('features', features);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <Input
                        placeholder="Icon (e.g., calendar, users, star, mic, camera)"
                        value={feature.icon || ''}
                        onChange={(e) => {
                          const features = [...(formData.data.features || [])];
                          features[index] = { ...features[index], icon: e.target.value };
                          updateData('features', features);
                        }}
                      />
                      <Input
                        placeholder="Title"
                        value={feature.title || ''}
                        onChange={(e) => {
                          const features = [...(formData.data.features || [])];
                          features[index] = { ...features[index], title: e.target.value };
                          updateData('features', features);
                        }}
                      />
                      <Textarea
                        placeholder="Description"
                        value={feature.description || ''}
                        onChange={(e) => {
                          const features = [...(formData.data.features || [])];
                          features[index] = { ...features[index], description: e.target.value };
                          updateData('features', features);
                        }}
                        rows={2}
                      />
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const features = [...(formData.data.features || []), { icon: '', title: '', description: '' }];
                  updateData('features', features);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Feature
              </Button>
            </TabsContent>
            
            {/* Speakers Tab */}
            <TabsContent value="speakers" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add speaker profiles for expo/conference pages</p>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.speakers || []).map((speaker, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Speaker {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const speakers = (formData.data.speakers || []).filter((_, i) => i !== index);
                            updateData('speakers', speakers);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <Input
                        placeholder="Name"
                        value={speaker.name || ''}
                        onChange={(e) => {
                          const speakers = [...(formData.data.speakers || [])];
                          speakers[index] = { ...speakers[index], name: e.target.value };
                          updateData('speakers', speakers);
                        }}
                      />
                      <Input
                        placeholder="Title / Company"
                        value={speaker.title || ''}
                        onChange={(e) => {
                          const speakers = [...(formData.data.speakers || [])];
                          speakers[index] = { ...speakers[index], title: e.target.value };
                          updateData('speakers', speakers);
                        }}
                      />
                      <Input
                        placeholder="Talk Topic"
                        value={speaker.topic || ''}
                        onChange={(e) => {
                          const speakers = [...(formData.data.speakers || [])];
                          speakers[index] = { ...speakers[index], topic: e.target.value };
                          updateData('speakers', speakers);
                        }}
                      />
                      <Input
                        placeholder="Image URL"
                        value={speaker.image || ''}
                        onChange={(e) => {
                          const speakers = [...(formData.data.speakers || [])];
                          speakers[index] = { ...speakers[index], image: e.target.value };
                          updateData('speakers', speakers);
                        }}
                      />
                      {speaker.image && (
                        <img src={speaker.image} alt={speaker.name} className="w-16 h-16 rounded-full object-cover" />
                      )}
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const speakers = [...(formData.data.speakers || []), { name: '', title: '', topic: '', image: '' }];
                  updateData('speakers', speakers);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Speaker
              </Button>
            </TabsContent>
            
            {/* Schedule Tab */}
            <TabsContent value="schedule" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add schedule/agenda items for events (displayed at 60% width with speaker images)</p>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.schedule || []).map((item, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Schedule Item {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const schedule = (formData.data.schedule || []).filter((_, i) => i !== index);
                            updateData('schedule', schedule);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Input
                            placeholder="Time (e.g., 11:00 AM - 12:00 PM)"
                            value={item.time || ''}
                            onChange={(e) => {
                              const schedule = [...(formData.data.schedule || [])];
                              schedule[index] = { ...schedule[index], time: e.target.value };
                              updateData('schedule', schedule);
                            }}
                          />
                          <Input
                            placeholder="Title"
                            value={item.title || ''}
                            onChange={(e) => {
                              const schedule = [...(formData.data.schedule || [])];
                              schedule[index] = { ...schedule[index], title: e.target.value };
                              updateData('schedule', schedule);
                            }}
                          />
                          <Input
                            placeholder="Speaker (optional)"
                            value={item.speaker || ''}
                            onChange={(e) => {
                              const schedule = [...(formData.data.schedule || [])];
                              schedule[index] = { ...schedule[index], speaker: e.target.value };
                              updateData('schedule', schedule);
                            }}
                          />
                        </div>
                        <div>
                          <ImageUpload
                            value={item.image || ''}
                            onChange={(url) => {
                              const schedule = [...(formData.data.schedule || [])];
                              schedule[index] = { ...schedule[index], image: url };
                              updateData('schedule', schedule);
                            }}
                            label="Speaker Image"
                          />
                        </div>
                      </div>
                      <Textarea
                        placeholder="Description (optional)"
                        value={item.description || ''}
                        onChange={(e) => {
                          const schedule = [...(formData.data.schedule || [])];
                          schedule[index] = { ...schedule[index], description: e.target.value };
                          updateData('schedule', schedule);
                        }}
                        rows={2}
                      />
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const schedule = [...(formData.data.schedule || []), { time: '', title: '', speaker: '', description: '', image: '' }];
                  updateData('schedule', schedule);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Schedule Item
              </Button>
            </TabsContent>
            
            {/* Tickets Tab */}
            <TabsContent value="tickets" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add ticket tiers for events</p>
              
              {/* Section-level ticket embed code */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  3rd Party Ticket Embed Code (Optional)
                </label>
                <p className="text-xs text-blue-600 mb-2">
                  Paste HTML/JavaScript code from Eventbrite, Luma, or other ticketing platforms. 
                  This will display below tickets that have empty CTA links.
                </p>
                <Textarea
                  placeholder="<!-- Paste your embed code here -->\n<script src='...'></script>"
                  value={formData.data.ticket_embed_code || ''}
                  onChange={(e) => updateData('ticket_embed_code', e.target.value)}
                  rows={6}
                  className="font-mono text-xs"
                />
              </div>
              
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.tickets || []).map((ticket, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Ticket {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const tickets = (formData.data.tickets || []).filter((_, i) => i !== index);
                            updateData('tickets', tickets);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <Input
                        placeholder="Ticket Name (e.g., VIP Pass)"
                        value={ticket.name || ''}
                        onChange={(e) => {
                          const tickets = [...(formData.data.tickets || [])];
                          tickets[index] = { ...tickets[index], name: e.target.value };
                          updateData('tickets', tickets);
                        }}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Price (e.g., $99)"
                          value={ticket.price || ''}
                          onChange={(e) => {
                            const tickets = [...(formData.data.tickets || [])];
                            tickets[index] = { ...tickets[index], price: e.target.value };
                            updateData('tickets', tickets);
                          }}
                        />
                        <Input
                          placeholder="Original Price (optional)"
                          value={ticket.original_price || ''}
                          onChange={(e) => {
                            const tickets = [...(formData.data.tickets || [])];
                            tickets[index] = { ...tickets[index], original_price: e.target.value };
                            updateData('tickets', tickets);
                          }}
                        />
                      </div>
                      <Input
                        placeholder="Badge (e.g., BEST VALUE)"
                        value={ticket.badge || ''}
                        onChange={(e) => {
                          const tickets = [...(formData.data.tickets || [])];
                          tickets[index] = { ...tickets[index], badge: e.target.value };
                          updateData('tickets', tickets);
                        }}
                      />
                      <Textarea
                        placeholder="Features (one per line)"
                        value={(ticket.features || []).join('\n')}
                        onChange={(e) => {
                          const tickets = [...(formData.data.tickets || [])];
                          tickets[index] = { ...tickets[index], features: e.target.value.split('\n').filter(f => f.trim()) };
                          updateData('tickets', tickets);
                        }}
                        rows={4}
                      />
                      <div className="border-t pt-3 mt-2">
                        <p className="text-xs text-gray-500 mb-2">CTA Button (leave empty to use embed code above)</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="CTA Label (e.g., Buy Now)"
                            value={ticket.cta_label || ''}
                            onChange={(e) => {
                              const tickets = [...(formData.data.tickets || [])];
                              tickets[index] = { ...tickets[index], cta_label: e.target.value };
                              updateData('tickets', tickets);
                            }}
                          />
                          <Input
                            placeholder="CTA Link (or leave empty for embed)"
                            value={ticket.cta_link || ''}
                            onChange={(e) => {
                              const tickets = [...(formData.data.tickets || [])];
                              tickets[index] = { ...tickets[index], cta_link: e.target.value };
                              updateData('tickets', tickets);
                            }}
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={ticket.highlighted || false}
                          onChange={(e) => {
                            const tickets = [...(formData.data.tickets || [])];
                            tickets[index] = { ...tickets[index], highlighted: e.target.checked };
                            updateData('tickets', tickets);
                          }}
                          className="rounded"
                        />
                        Highlight this ticket (dark background)
                      </label>
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const tickets = [...(formData.data.tickets || []), { name: '', price: '', features: [], cta_label: '', cta_link: '', highlighted: false }];
                  updateData('tickets', tickets);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Ticket
              </Button>
            </TabsContent>
            
            {/* Testimonials Tab */}
            <TabsContent value="testimonials" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add testimonials/reviews with author photos</p>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.testimonials || []).map((testimonial, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Testimonial {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const testimonials = (formData.data.testimonials || []).filter((_, i) => i !== index);
                            updateData('testimonials', testimonials);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Quote / Review"
                        value={testimonial.quote || ''}
                        onChange={(e) => {
                          const testimonials = [...(formData.data.testimonials || [])];
                          testimonials[index] = { ...testimonials[index], quote: e.target.value };
                          updateData('testimonials', testimonials);
                        }}
                        rows={3}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Input
                            placeholder="Author Name"
                            value={testimonial.author || ''}
                            onChange={(e) => {
                              const testimonials = [...(formData.data.testimonials || [])];
                              testimonials[index] = { ...testimonials[index], author: e.target.value };
                              updateData('testimonials', testimonials);
                            }}
                          />
                          <Input
                            placeholder="Author Title / Company"
                            value={testimonial.title || ''}
                            onChange={(e) => {
                              const testimonials = [...(formData.data.testimonials || [])];
                              testimonials[index] = { ...testimonials[index], title: e.target.value };
                              updateData('testimonials', testimonials);
                            }}
                          />
                        </div>
                        <div>
                          <ImageUpload
                            value={testimonial.image || ''}
                            onChange={(url) => {
                              const testimonials = [...(formData.data.testimonials || [])];
                              testimonials[index] = { ...testimonials[index], image: url };
                              updateData('testimonials', testimonials);
                            }}
                            label="Author Photo"
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const testimonials = [...(formData.data.testimonials || []), { quote: '', author: '', title: '', image: '' }];
                  updateData('testimonials', testimonials);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Testimonial
              </Button>
            </TabsContent>
            
            {/* FAQs Tab */}
            <TabsContent value="faqs" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add frequently asked questions</p>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.faqs || []).map((faq, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">FAQ {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const faqs = (formData.data.faqs || []).filter((_, i) => i !== index);
                            updateData('faqs', faqs);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <Input
                        placeholder="Question"
                        value={faq.question || ''}
                        onChange={(e) => {
                          const faqs = [...(formData.data.faqs || [])];
                          faqs[index] = { ...faqs[index], question: e.target.value };
                          updateData('faqs', faqs);
                        }}
                      />
                      <Textarea
                        placeholder="Answer"
                        value={faq.answer || ''}
                        onChange={(e) => {
                          const faqs = [...(formData.data.faqs || [])];
                          faqs[index] = { ...faqs[index], answer: e.target.value };
                          updateData('faqs', faqs);
                        }}
                        rows={3}
                      />
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const faqs = [...(formData.data.faqs || []), { question: '', answer: '' }];
                  updateData('faqs', faqs);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add FAQ
              </Button>
            </TabsContent>
            
            {/* Steps Tab */}
            <TabsContent value="steps" className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">Add numbered steps/process (e.g., "How to Host an Event")</p>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {(formData.data.steps || []).map((step, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">Step {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const steps = (formData.data.steps || []).filter((_, i) => i !== index);
                            updateData('steps', steps);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Number</label>
                          <Input
                            placeholder="1"
                            value={step.number || ''}
                            onChange={(e) => {
                              const steps = [...(formData.data.steps || [])];
                              steps[index] = { ...steps[index], number: e.target.value };
                              updateData('steps', steps);
                            }}
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="block text-xs text-gray-500 mb-1">Title</label>
                          <Input
                            placeholder="Step Title"
                            value={step.title || ''}
                            onChange={(e) => {
                              const steps = [...(formData.data.steps || [])];
                              steps[index] = { ...steps[index], title: e.target.value };
                              updateData('steps', steps);
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Description (HTML supported)</label>
                        <Textarea
                          placeholder="Step description..."
                          value={step.description || ''}
                          onChange={(e) => {
                            const steps = [...(formData.data.steps || [])];
                            steps[index] = { ...steps[index], description: e.target.value };
                            updateData('steps', steps);
                          }}
                          rows={2}
                        />
                        <p className="text-xs text-gray-400 mt-1">Supports: &lt;b&gt;, &lt;i&gt;, &lt;a href=""&gt;, &lt;br&gt;</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              <Button
                onClick={() => {
                  const steps = formData.data.steps || [];
                  const nextNum = steps.length + 1;
                  updateData('steps', [...steps, { number: String(nextNum), title: '', description: '' }]);
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Step
              </Button>
            </TabsContent>
          </Tabs>
        );

      case 'image_text':
        return (
          <div className="space-y-4">
            {anchorField}
            <ImageUpload
              value={formData.data.image || ''}
              onChange={(url) => updateData('image', url)}
              label="Image"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Position</label>
              <Select
                value={formData.data.image_position || 'left'}
                onValueChange={(value) => updateData('image_position', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
              <Input
                value={formData.data.heading || ''}
                onChange={(e) => updateData('heading', e.target.value)}
                placeholder="Section Heading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subheading</label>
              <Input
                value={formData.data.subheading || ''}
                onChange={(e) => updateData('subheading', e.target.value)}
                placeholder="Optional subheading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <Textarea
                value={formData.data.content || ''}
                onChange={(e) => updateData('content', e.target.value)}
                placeholder="Your content here..."
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'video_text':
        return (
          <div className="space-y-4">
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video Source</label>
              <Tabs 
                value={formData.data.video_source || 'embed'} 
                onValueChange={(value) => updateData('video_source', value)}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="embed">Embed Code</TabsTrigger>
                  <TabsTrigger value="upload">Upload Video</TabsTrigger>
                </TabsList>
                <TabsContent value="embed" className="mt-3">
                  <Textarea
                    value={formData.data.video_embed || ''}
                    onChange={(e) => updateData('video_embed', e.target.value)}
                    rows={4}
                    placeholder='<iframe src="https://www.youtube.com/embed/..." frameborder="0" allowfullscreen></iframe>'
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste YouTube or Vimeo embed code here</p>
                </TabsContent>
                <TabsContent value="upload" className="mt-3">
                  <ImageUpload
                    value={formData.data.video_url || ''}
                    onChange={(url) => updateData('video_url', url)}
                    label="Video File"
                    accept="video/*"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload MP4, WebM, or other video formats</p>
                </TabsContent>
              </Tabs>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video Position</label>
              <Select
                value={formData.data.video_position || 'left'}
                onValueChange={(value) => updateData('video_position', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
              <Input
                value={formData.data.heading || ''}
                onChange={(e) => updateData('heading', e.target.value)}
                placeholder="Section Heading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subheading</label>
              <Input
                value={formData.data.subheading || ''}
                onChange={(e) => updateData('subheading', e.target.value)}
                placeholder="Optional subheading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <Textarea
                value={formData.data.content || ''}
                onChange={(e) => updateData('content', e.target.value)}
                placeholder="Your content here..."
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <Textarea
                value={formData.data.content || ''}
                onChange={(e) => updateData('content', e.target.value)}
                placeholder="Your text content here..."
                rows={10}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;h1&gt;-&lt;h6&gt;, &lt;p&gt;, &lt;a&gt;, &lt;b&gt;, &lt;i&gt;, &lt;span&gt;, &lt;div&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;hr&gt;</p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'embed':
        return (
          <div className="space-y-4">
            {anchorField}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title
                </label>
                <Input
                  value={formData.data.section_title || ''}
                  onChange={(e) => updateData('section_title', e.target.value)}
                  placeholder="Contact Us"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Subtitle
                </label>
                <Input
                  value={formData.data.section_subtitle || ''}
                  onChange={(e) => updateData('section_subtitle', e.target.value)}
                  placeholder="Get in touch with our team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Description
                </label>
                <Textarea
                  value={formData.data.section_description || ''}
                  onChange={(e) => updateData('section_description', e.target.value)}
                  placeholder="Fill out the form below and we'll get back to you within 24 hours."
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Embed Code
              </label>
              <Textarea
                value={formData.data.embed_code || ''}
                onChange={(e) => updateData('embed_code', e.target.value)}
                placeholder={`<!-- Examples: -->
<iframe src="https://www.youtube.com/embed/..." frameborder="0" allowfullscreen></iframe>

<!-- Or JavaScript embeds like Close CRM forms: -->
<script src="https://webforms.closeiocdn.com/webforms.js" type="module" crossorigin="anonymous" defer></script>
<close-form id="form_xxx"></close-form>`}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-sm text-gray-500 mt-2">
                Paste any embed code: YouTube/Vimeo iframes, JavaScript widgets, form embeds (Close CRM, HubSpot, etc.), or custom HTML/JS
              </p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Embed Width (Desktop)
                </label>
                <Select
                  value={formData.data.embed_width || '60'}
                  onValueChange={(value) => updateData('embed_width', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select width" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="40">40%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="60">60% (Default)</SelectItem>
                    <SelectItem value="70">70%</SelectItem>
                    <SelectItem value="80">80%</SelectItem>
                    <SelectItem value="90">90%</SelectItem>
                    <SelectItem value="100">100% (Full Width)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Width on desktop screens. Always full width on mobile.</p>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-4">
            {anchorField}
            <GalleryEditor formData={formData} updateData={updateData} toast={toast} />
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="View All Photos"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'sponsors':
        return (
          <div className="space-y-4">
            {anchorField}
            <SponsorsEditor formData={formData} updateData={updateData} toast={toast} />
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Become a Sponsor"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'press':
        return (
          <div className="space-y-4">
            {anchorField}
            <PressEditor formData={formData} updateData={updateData} toast={toast} />
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Read More Coverage"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </div>
        );

      case 'newsletter_flodesk':
        return (
          <div className="space-y-4">
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Flodesk Form ID</label>
              <Input
                value={formData.data.flodesk_form_id || '698ff949955577ffd7fda382'}
                onChange={(e) => updateData('flodesk_form_id', e.target.value)}
                placeholder="698ff949955577ffd7fda382"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find your Form ID in Flodesk: Forms → Select Form → Share → Get the ID from the embed code
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title (optional)</label>
              <Input
                value={formData.data.section_title || ''}
                onChange={(e) => updateData('section_title', e.target.value)}
                placeholder="Leave empty to hide"
              />
              <p className="text-xs text-gray-500 mt-1">Shows above the Flodesk form. Leave empty to only use Flodesk's built-in title.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle (optional)</label>
              <Input
                value={formData.data.section_subtitle || ''}
                onChange={(e) => updateData('section_subtitle', e.target.value)}
                placeholder="Leave empty to hide"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text (with checkmark)</label>
              <Input
                value={formData.data.bottom_text || 'Join 40,000+ subscribers already in our community'}
                onChange={(e) => updateData('bottom_text', e.target.value)}
                placeholder="Join 40,000+ subscribers already in our community"
              />
              <p className="text-xs text-gray-500 mt-1">Shows below the form with a green checkmark. Leave empty to hide.</p>
            </div>
          </div>
        );

      default:
        return <p className="text-gray-500">Section editor for {formData.type} coming soon...</p>;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Type</label>
        <Select
          value={formData.type}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, type: value }))}
          disabled={!!section}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SECTION_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title (internal)</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Internal reference name"
        />
      </div>

      {renderFields()}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} className="bg-black hover:bg-gray-800">
          <Save size={16} className="mr-2" />
          Save Section
        </Button>
      </DialogFooter>
    </div>
  );
}

// Sortable Gallery Image Component
function SortableGalleryImage({ id, image, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative group">
      <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100">
        <img
          src={image}
          alt={`Gallery ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
        <div {...listeners} className="cursor-move bg-white/90 rounded-full p-2">
          <GripVertical size={16} className="text-gray-600" />
        </div>
        <button
          onClick={() => onRemove(index)}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2"
        >
          <Trash2 size={16} />
        </button>
      </div>
      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-1 rounded">
        {index + 1}
      </div>
    </div>
  );
}

// Gallery Editor Component with Bulk Upload and Reorder
function GalleryEditor({ formData, updateData, toast }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const images = formData.data.images || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = images.findIndex((_, i) => `img-${i}` === active.id);
      const newIndex = images.findIndex((_, i) => `img-${i}` === over.id);
      updateData('images', arrayMove(images, oldIndex, newIndex));
    }
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate files
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: 'File too large', description: `${file.name} exceeds 5MB limit`, variant: 'destructive' });
        return false;
      }
      if (!file.type.startsWith('image/')) {
        toast({ title: 'Invalid file', description: `${file.name} is not an image`, variant: 'destructive' });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of validFiles) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await api.post('/admin/upload', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedUrls.push(response.data.url);
      } catch (error) {
        console.error('Upload error:', error);
        toast({ title: 'Upload failed', description: `Failed to upload ${file.name}`, variant: 'destructive' });
      }
    }

    if (uploadedUrls.length > 0) {
      updateData('images', [...images, ...uploadedUrls]);
      toast({ title: 'Success', description: `Uploaded ${uploadedUrls.length} image(s)` });
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSingleUpload = (url) => {
    if (url) {
      updateData('images', [...images, url]);
    }
  };

  const removeImage = async (index) => {
    const imageUrl = images[index];
    // Try to delete from S3 if it's an S3 URL
    if (imageUrl && imageUrl.includes('s3') && imageUrl.includes('amazonaws.com')) {
      try {
        await api.delete('/admin/upload', { data: { url: imageUrl } });
      } catch (error) {
        console.error('S3 delete error:', error);
        // Continue with removal even if S3 delete fails
      }
    }
    updateData('images', images.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
          <Input
            value={formData.data.section_title || ''}
            onChange={(e) => updateData('section_title', e.target.value)}
            placeholder="Event Gallery"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
          <Textarea
            value={formData.data.section_description || ''}
            onChange={(e) => updateData('section_description', e.target.value)}
            placeholder="Highlights from our events"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
        </div>
      </div>

      {/* Bulk Upload Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleBulkUpload}
          className="hidden"
          disabled={uploading}
        />
        <div className="text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4 flex flex-col items-center gap-2">
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
            >
              <ImageIcon size={16} className="mr-2" />
              {uploading ? 'Uploading...' : 'Bulk Upload Images'}
            </Button>
            <p className="text-xs text-gray-500">
              Select multiple images at once (PNG, JPG, WebP up to 5MB each)
            </p>
          </div>
        </div>
      </div>

      {/* Single Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Or Add Single Image</label>
        <ImageUpload
          value=""
          onChange={handleSingleUpload}
          label=""
          showPreview={false}
        />
      </div>

      {/* Image Gallery with Drag & Drop Reorder */}
      {images.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Gallery Images ({images.length})
            </label>
            <p className="text-xs text-gray-500">Drag to reorder</p>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((_, i) => `img-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-4 gap-3 max-h-80 overflow-y-auto p-1">
                {images.map((image, index) => (
                  <SortableGalleryImage
                    key={`img-${index}`}
                    id={`img-${index}`}
                    image={image}
                    index={index}
                    onRemove={removeImage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

// Sponsors Editor Component with Logo Upload and Links
function SponsorsEditor({ formData, updateData, toast }) {
  const [uploading, setUploading] = useState(false);
  const sponsors = formData.data.sponsors || [];

  const handleLogoUpload = async (index, file) => {
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Logo must be under 5MB', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/admin/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const updated = [...sponsors];
      updated[index] = { ...updated[index], logo: response.data.url };
      updateData('sponsors', updated);
      toast({ title: 'Success', description: 'Logo uploaded' });
    } catch (error) {
      toast({ title: 'Upload failed', description: 'Could not upload logo', variant: 'destructive' });
    }
    setUploading(false);
  };

  const addSponsor = () => {
    updateData('sponsors', [...sponsors, { logo: '', name: '', url: '' }]);
  };

  const updateSponsor = (index, field, value) => {
    const updated = [...sponsors];
    updated[index] = { ...updated[index], [field]: value };
    updateData('sponsors', updated);
  };

  const removeSponsor = (index) => {
    updateData('sponsors', sponsors.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
          <Input
            value={formData.data.section_title || ''}
            onChange={(e) => updateData('section_title', e.target.value)}
            placeholder="Our Sponsors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
          <Textarea
            value={formData.data.section_description || ''}
            onChange={(e) => updateData('section_description', e.target.value)}
            placeholder="Thank you to our partners"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto space-y-3">
        {sponsors.map((sponsor, index) => (
          <Card key={index} className="p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="font-medium text-sm">Sponsor {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeSponsor(index)}
                className="text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Sponsor Name (optional)</label>
                  <Input
                    placeholder="Company Name"
                    value={sponsor.name || ''}
                    onChange={(e) => updateSponsor(index, 'name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Website URL (optional)</label>
                  <Input
                    placeholder="https://sponsor-website.com"
                    value={sponsor.url || ''}
                    onChange={(e) => updateSponsor(index, 'url', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Logo</label>
                <ImageUpload
                  value={sponsor.logo || ''}
                  onChange={(url) => updateSponsor(index, 'logo', url)}
                  label=""
                />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Button onClick={addSponsor} variant="outline" size="sm" className="w-full">
        <Plus size={16} className="mr-2" />
        Add Sponsor
      </Button>
    </div>
  );
}

// Press Editor Component for Media Coverage
function PressEditor({ formData, updateData, toast }) {
  const pressItems = formData.data.press_items || [];

  const addPressItem = () => {
    updateData('press_items', [...pressItems, { quote: '', publication: '', date: '', article_name: '', article_link: '' }]);
  };

  const updatePressItem = (index, field, value) => {
    const updated = [...pressItems];
    updated[index] = { ...updated[index], [field]: value };
    updateData('press_items', updated);
  };

  const removePressItem = (index) => {
    updateData('press_items', pressItems.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
          <Input
            value={formData.data.section_title || ''}
            onChange={(e) => updateData('section_title', e.target.value)}
            placeholder="In The Press"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Subtitle</label>
          <Input
            value={formData.data.section_subtitle || ''}
            onChange={(e) => updateData('section_subtitle', e.target.value)}
            placeholder="Latest Coverage"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
        <Textarea
          value={formData.data.section_description || ''}
          onChange={(e) => updateData('section_description', e.target.value)}
          placeholder="What the media is saying about us"
          rows={2}
        />
        <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
      </div>

      <div className="max-h-[500px] overflow-y-auto space-y-3">
        {pressItems.map((item, index) => (
          <Card key={index} className="p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="font-medium text-sm">Press Quote {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removePressItem(index)}
                className="text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Quote Text *</label>
                <Textarea
                  placeholder="The event was remarkable..."
                  value={item.quote || ''}
                  onChange={(e) => updatePressItem(index, 'quote', e.target.value)}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Publication Name *</label>
                  <Input
                    placeholder="TechBullion"
                    value={item.publication || ''}
                    onChange={(e) => updatePressItem(index, 'publication', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <Input
                    placeholder="Mar 18, 2025"
                    value={item.date || ''}
                    onChange={(e) => updatePressItem(index, 'date', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Article Title</label>
                <Input
                  placeholder="A Day at the Brooklyn Tech Expo..."
                  value={item.article_name || ''}
                  onChange={(e) => updatePressItem(index, 'article_name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Article Link (URL)</label>
                <Input
                  placeholder="https://techbullion.com/..."
                  value={item.article_link || ''}
                  onChange={(e) => updatePressItem(index, 'article_link', e.target.value)}
                />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Button onClick={addPressItem} variant="outline" size="sm" className="w-full">
        <Plus size={16} className="mr-2" />
        Add Press Quote
      </Button>
    </div>
  );
}

// Main Page Builder Component
const PageBuilder = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPageAndSections();
  }, [pageId]);

  const fetchPageAndSections = async () => {
    try {
      const [pagesRes, sectionsRes] = await Promise.all([
        api.get('/admin/pages'),
        api.get(`/admin/pages/${pageId}/sections`),
      ]);
      const foundPage = pagesRes.data.find((p) => p.id === pageId);
      setPage(foundPage);
      setSections(sectionsRes.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch page data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        const reordered = newItems.map((item, index) => ({ ...item, order: index }));
        saveOrder(reordered);
        return reordered;
      });
    }
  };

  const saveOrder = async (reorderedSections) => {
    try {
      await api.post(`/admin/pages/${pageId}/sections/reorder`, {
        items: reorderedSections.map((s) => ({ id: s.id, order: s.order })),
      });
      toast({ title: 'Success', description: 'Section order updated' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update section order',
        variant: 'destructive',
      });
    }
  };

  const handleCreate = () => {
    setEditingSection(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setIsDialogOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingSection) {
        await api.put(`/admin/pages/sections/${editingSection.id}`, formData);
        toast({ title: 'Success', description: 'Section updated' });
      } else {
        await api.post(`/admin/pages/${pageId}/sections`, {
          ...formData,
          page_id: pageId,
          order: sections.length,
          is_enabled: true,
        });
        toast({ title: 'Success', description: 'Section created' });
      }
      setIsDialogOpen(false);
      fetchPageAndSections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save section',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    try {
      await api.delete(`/admin/pages/sections/${id}`);
      toast({ title: 'Success', description: 'Section deleted' });
      fetchPageAndSections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete section',
        variant: 'destructive',
      });
    }
  };

  const handleToggle = async (id, isEnabled) => {
    try {
      await api.put(`/admin/pages/sections/${id}`, { is_enabled: isEnabled });
      toast({
        title: 'Success',
        description: isEnabled ? 'Section enabled' : 'Section disabled',
      });
      fetchPageAndSections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle section',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (!page) {
    return (
      <div className="p-8">
        <p>Page not found</p>
        <Button onClick={() => navigate('/admin/pages')} className="mt-4">
          Back to Pages
        </Button>
      </div>
    );
  }

  const pageType = PAGE_TYPES[page.page_type] || PAGE_TYPES.regular;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/admin/pages')} size="sm">
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900">{page.title}</h1>
              <Badge className={pageType.color}>{pageType.label}</Badge>
              <Badge variant={page.status === 'published' ? 'default' : 'secondary'}>
                {page.status}
              </Badge>
            </div>
            <p className="text-gray-600 mt-1">
              /{page.slug} • {sections.length} sections
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {page.status === 'published' && (
            <Button
              variant="outline"
              onClick={() => window.open(`/${page.slug}`, '_blank')}
            >
              <ExternalLink size={16} className="mr-2" />
              View Page
            </Button>
          )}
          <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
            <Plus size={20} className="mr-2" />
            Add Section
          </Button>
        </div>
      </div>

      {sections.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-xl text-gray-600 mb-4">No sections yet</p>
          <p className="text-gray-500 mb-6">Start building your page by adding sections</p>
          <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
            <Plus size={20} className="mr-2" />
            Create Your First Section
          </Button>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? 'Edit Section' : 'Create New Section'}
            </DialogTitle>
          </DialogHeader>
          <SectionEditor
            section={editingSection}
            onSave={handleSave}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBuilder;
